<template>
    <div class="form-type-0">
        <form @submit.prevent="addProspectsBulk" @input="onInputChange" ref="form" novalidate>
            <CustomSelect
                v-model="count"
                inputKey="audience_bulk_count"
                :options="options"
                ref="bulk_count"
                required
            />
            <CustomToggle
                v-model="misc_options"
                inputKey="options_warning"
                ref="options-warning"
                :options="warningOptions"
                required
            />
            <button type="submit" :disabled="loading">
                <span v-if="loading" class="loading-spinner-type-1"></span>
                <span v-else>{{ $t('forms.audience_bulk.button') }}</span>
            </button>
            <p v-if="formError" class="form-error">{{ $t(formError.message && $te(`forms.audience_bulk.errors.${formError.message}`) ? `forms.audience_bulk.errors.${formError.message}` : 'forms.audience_bulk.errors.default') }}</p>
        </form>
    </div>
</template>

<script>
    import { useAccountStore } from '@/stores/account';
    import { useAudienceStore } from '@/stores/audience';
    import formMixin from '@/mixins/formMixin';
    import CustomSelect from '@/components/elements/inputs/CustomSelect';
    import CustomToggle from '@/components/elements/inputs/CustomToggle';
    import { formatNumber } from '@/utils/formatting';

    export default {
        mixins: [formMixin],
        emits: ['form_submission_successful'],
        components: {
            CustomSelect,
            CustomToggle
        },
        props: {
            credits: {
                type: [Number, Boolean],
                default: 0
            },
            suspects: {
                type: [Number, Boolean],
                default: 0
            },
        },
        data() {
            return {
                count: 10,
                misc_options: [],
                warningOptions: [
                    { value: 'w-1', text: 'fields.audience_bulk_start_warning.options.warning', required: true },
                    { value: 'w-2', text: 'fields.audience_bulk_start_warning.options.warning_2', required: true },
                ]
            };
        },
        computed: {
            accountStore() {
                return useAccountStore();
            },
            audienceStore() {
                return useAudienceStore();
            },
            max() {
                return Math.min(this.credits, this.suspects);
            },
            maxType() {
                return this.suspects > this.credits ? 'credits' : 'suspects';
            },
            countOptions() {
                if(this.max > 10000) {
                    return [100, 250, 500, 1000, 2500, 5000, 10000];
                } else if (this.max > 5000) {
                    return [50, 100, 250, 500, 1000, 2500, 5000];
                } else if (this.max > 1000) {
                    return [25, 50, 100, 250, 500, 1000];
                } else if (this.max > 500) {
                    return [10, 25, 50, 100, 250];
                } else if (this.max > 100) {
                    return [10, 25, 50, 100];
                } else if (this.max > 50) {
                    return [5, 10, 25, 50];
                } else if (this.max > 10) {
                    return [5, 10];
                } else if (this.max > 5) {
                    return [5];
                } else {
                    return [];
                }
            },
            options() {
                const options = this.countOptions.map(option => {
                    return {
                        value: option,
                        text: this.$t('fields.audience_bulk_count.options.default', { count: formatNumber(option) }),
                        noTranslate: true
                    };
                });

                options.push({
                    value: this.max,
                    text: this.$t(`fields.audience_bulk_count.options.${this.maxType === 'credits' ? 'all_credits' : 'all_prospects'}`, { count: formatNumber(this.max) }),
                    noTranslate: true
                });

                return options;
            }
        },
        methods: {
            async addProspectsBulk() {
                const form = this.$refs.form;
                if(form.checkValidity()) {
                    this.loading = true;

                    const success = await this.audienceStore.addProspectsInBulk(this.count);
                    this.loading = false;
                    
                    if (success === true) {
                        form.reset();
                        this.formError = false;
                        this.$emit('form_submission_successful');
                    } else if (typeof success === 'string') {
                        this.formError = {
                            message: success
                        };
                    } else {
                        this.formError = true;
                    }
                } else {
                    this.validateAllFields();
                }
            }
        },
        mounted() {
            this.count = this.options[0].value;
        }
    }
</script>